import { computed, Directive, input } from '@angular/core';

import { MibPalette, MibTheme } from '../../foundations';

export type BadgeTheme = MibTheme;
export type BadgeMode = 'filled' | 'subtle';
export type BadgeShape = 'round' | 'square';
export type BadgeSize = 'sm' | 'md' | 'lg';

/**
 * Each of the ten palettes as a badge, filled and subtle: the 700 step of the ramp, and that same
 * step at 12% under itself as ink. Inside a `.dark` ancestor the subtle skin switches on its own to
 * the 300 step as ink over the 400 at 16%, which holds AA on gray-900 and gray-800.
 *
 * Translucent and not a flat 50, for the reason the `primary` tint is: a badge is a mark laid *on* a
 * surface, and the rows it sits in are not all the same colour — a marked row of a record is drawn
 * on `mib-danger-surface`. A flat ground floats on it; a tint takes it into account.
 *
 * Exported because the mark is not always a badge: a component drawing a chip of its own takes the
 * skin from here rather than writing the ten pairs a second time.
 */
/* eslint-disable mib/no-palette-colour -- badge palettes: data, not interface colours */
export const MIB_BADGE_PALETTE_SKIN: Record<MibPalette, { filled: string; subtle: string }> = {
  navy: {
    filled: 'bg-mib-branding-700 text-white',
    subtle: 'bg-mib-branding-700/12 text-mib-branding-700 dark:bg-mib-branding-400/16 dark:text-mib-branding-300',
  },
  indigo: {
    filled: 'bg-mib-indigo-700 text-white',
    subtle: 'bg-mib-indigo-700/12 text-mib-indigo-700 dark:bg-mib-indigo-400/16 dark:text-mib-indigo-300',
  },
  grey: {
    filled: 'bg-gray-700 text-white',
    subtle: 'bg-gray-700/12 text-gray-700 dark:bg-gray-400/16 dark:text-gray-300',
  },
  green: {
    filled: 'bg-green-700 text-white',
    subtle: 'bg-green-700/12 text-green-700 dark:bg-green-400/16 dark:text-green-300',
  },
  /*
   The one exception to the 700 step, and the one fill of the ten that does not hold AA under white.

   orange-700 reads brown beside the other nine, which is wrong for a colour whose whole job is to
   separate one kind from the next, so the kind is drawn on 600. White reaches 3.6:1 there against
   the 4.5 the scale asks, and that is accepted **here and nowhere else**. The subtle mode keeps the
   700, because its step is read as text and text has no exception.
  */
  orange: {
    filled: 'bg-orange-600 text-white',
    subtle: 'bg-orange-700/12 text-orange-700 dark:bg-orange-400/16 dark:text-orange-300',
  },
  red: { filled: 'bg-red-700 text-white', subtle: 'bg-red-700/12 text-red-700 dark:bg-red-400/16 dark:text-red-300' },
  cyan: {
    filled: 'bg-cyan-700 text-white',
    subtle: 'bg-cyan-700/12 text-cyan-700 dark:bg-cyan-400/16 dark:text-cyan-300',
  },
  sky: { filled: 'bg-sky-700 text-white', subtle: 'bg-sky-700/12 text-sky-700 dark:bg-sky-400/16 dark:text-sky-300' },
  violet: {
    filled: 'bg-violet-700 text-white',
    subtle: 'bg-violet-700/12 text-violet-700 dark:bg-violet-400/16 dark:text-violet-300',
  },
  pink: {
    filled: 'bg-pink-700 text-white',
    subtle: 'bg-pink-700/12 text-pink-700 dark:bg-pink-400/16 dark:text-pink-300',
  },
};

/** Each theme as a solid ground and the ink that reads on it — for any mark filled with a theme. */
export const MIB_THEME_FILL: Record<MibTheme, { bg: string; text: string }> = {
  primary: { bg: 'bg-mib-primary', text: 'text-white' },
  secondary: { bg: 'bg-gray-700', text: 'text-white' },
  success: { bg: 'bg-mib-success', text: 'text-white' },
  warning: { bg: 'bg-mib-warning', text: 'text-gray-900' },
  danger: { bg: 'bg-mib-danger', text: 'text-white' },
  info: { bg: 'bg-mib-info', text: 'text-white' },
  ai: { bg: 'bg-mib-branding-700', text: 'text-white' },
};

/**
 * The fill step of each ramp on its own, for a mark that is a colour and nothing else — the dot a
 * filter chip carries, which has to be the very colour the badge of that same kind is filled with.
 *
 * It repeats the grounds above rather than deriving them, and it has to: Tailwind reads class names
 * out of the source text, so a class assembled at runtime is never generated. The badge spec holds
 * the two tables to each other.
 */
export const MIB_PALETTE_DOT: Record<MibPalette, string> = {
  navy: 'bg-mib-branding-700',
  indigo: 'bg-mib-indigo-700',
  grey: 'bg-gray-700',
  green: 'bg-green-700',
  orange: 'bg-orange-600',
  red: 'bg-red-700',
  cyan: 'bg-cyan-700',
  sky: 'bg-sky-700',
  violet: 'bg-violet-700',
  pink: 'bg-pink-700',
};
/** The dark half of each theme's subtle skin: a theme takes the palette of its own hue. */
const THEME_SUBTLE_DARK: Record<MibTheme, string> = {
  primary: 'dark:bg-mib-branding-400/16 dark:text-mib-branding-300',
  secondary: 'dark:bg-gray-400/16 dark:text-gray-300',
  success: 'dark:bg-green-400/16 dark:text-green-300',
  warning: 'dark:bg-orange-400/16 dark:text-orange-300',
  // Not `mib-danger-dark`: red-400 holds only 4.1:1 on gray-800.
  danger: 'dark:bg-red-400/16 dark:text-red-300',
  info: 'dark:bg-cyan-400/16 dark:text-cyan-300',
  ai: 'dark:bg-mib-branding-400/16 dark:text-mib-branding-300',
};
/* eslint-enable mib/no-palette-colour */

/*
 The badge is sized in `em`, so it takes the size of the text it sits in: the same badge is large
 in a `text-2xl` line and small in a table cell, without anyone choosing a step. The font size is a
 ratio of its surroundings; the height, the minimum width and the inset are then expressed against
 the badge's own font size, so the whole mark scales as one thing.

 The three steps are ratios too, not pixels — they say how loud the badge is next to its text, and
 the text still decides how big that is. The proportions are the ones Tailwind gives its own badge —
 a `text-xs` label, a `px-2` inset, a 24px box — expressed as ratios so they hold at any text size:
 in running text at 16px, `md` lands on exactly that, 12px of label in a 24px pill with an 8px
 inset.

 `leading-none` and `box-border` are what make the circle a circle, and neither is decoration.
 Taking the font size from the parent means inheriting its line height too, and a paragraph set in
 `leading-relaxed` gives the badge an intrinsic height taller than its own box — the mark comes out
 as a vertical pill instead of a round one. Pinning the line height cuts that inheritance, and
 `box-border` puts the inset inside the minimum width, so a lone figure sits in a box exactly as
 wide as it is tall whatever reset the host application ships.
*/
const METRICS: Record<BadgeSize, string> = {
  sm: 'text-[0.65em] h-[1.9em] min-w-[1.9em] px-[0.65em]',
  md: 'text-[0.75em] h-[2em] min-w-[2em] px-[0.7em]',
  lg: 'text-[0.875em] h-[2.1em] min-w-[2.1em] px-[0.75em]',
};

/** Cuts the inherited line height and the host's box model out of the geometry. */
const CIRCLE_GUARDS = 'leading-none box-border';

/**
 * The badge of the design system, applied to the element the meaning asks for.
 *
 * ```html
 * <span mibBadge theme="danger">Hot</span>
 * <a mibBadge theme="info" href="/news">New</a>
 * <p class="text-2xl">Procedural Intelligence <span mibBadge theme="secondary">New</span></p>
 * ```
 *
 * It draws nothing of its own — it is a skin over whatever element carries it — so it is a directive
 * rather than an element of ours. That is what lets the badge be a `<span>` in a sentence, an `<a>`
 * when it is clickable, or a `<td>` in a table, instead of an unknown `<mib-badge>` element that
 * says nothing to a screen reader.
 *
 * Its width follows its content: a word comes out as a pill, a single figure or letter as a circle
 * — the minimum width equals the height — and a badge with nothing in it as a dot. There is no
 * input for any of that.
 */
@Directive({
  selector: '[mibBadge]',
  host: {
    '[class]': 'hostClasses()',
  },
})
export class MibBadgeDirective {
  /** The colour of the badge, one of the seven themes. */
  readonly theme = input<BadgeTheme>('primary');

  /**
   * A colour that says **kind** instead of state, taken from the ten palettes of Foundations /
   * Colors. Set, it replaces {@link theme}; left null, the theme decides.
   *
   * The seven themes carry a judgement — a medication is not "a success", an imaging run is not "an
   * info" — and they are too few besides for a record that sorts its entries into eight kinds. Reach
   * for a palette when the colour means nothing but "not the same as the one beside it".
   */
  readonly palette = input<MibPalette | null>(null);

  /**
   * How the theme is drawn: `filled` fills with the theme colour, `subtle` sets the theme ink on
   * its own surface — the pairing alerts and field errors use.
   *
   * The same word the button uses for the same idea. It replaces a `contrast` input whose values
   * were `ascent` and `subtle`, `ascent` being a misspelling of `accent` that had reached the
   * public API.
   */
  readonly mode = input<BadgeMode>('filled');

  /**
   * `round` — a counter bubble, a dot, a pill — or `square`, on the 4px radius of the design system.
   *
   * A closed set and not a class: the previous input took any Tailwind radius, which is how the
   * documentation came to ask for `full-rounded`, a class that does not exist, and got square badges
   * for years without anyone seeing it.
   */
  readonly shape = input<BadgeShape>('round');

  /**
   * How loud the badge is next to its text — `sm`, `md`, `lg`, the steps of the button.
   *
   * They are ratios of the surrounding text and not pixels, so the size input and the scaling live
   * together: `lg` in a `text-sm` row is still smaller than `sm` in a `text-2xl` one.
   */
  readonly size = input<BadgeSize>('md');

  protected readonly hostClasses = computed(() => {
    const classes = [
      'inline-flex',
      'items-center',
      'justify-center',
      'font-medium',
      CIRCLE_GUARDS,
      METRICS[this.size()],
    ];

    const theme = this.theme();
    const palette = this.palette();
    const mode = this.mode();

    if (palette) {
      classes.push(MIB_BADGE_PALETTE_SKIN[palette][mode]);
      classes.push(this.shape() === 'square' ? 'rounded-mib' : 'rounded-full');
      return classes.join(' ');
    }

    if (mode === 'filled') {
      classes.push(MIB_THEME_FILL[theme].bg, MIB_THEME_FILL[theme].text);
    } else {
      const subtle: Record<BadgeTheme, { bg: string; text: string }> = {
        /*
         The primary tint is the primary itself at 15%, and not a flat `mib-branding-50` it happens
         to resemble on white. A flat tint is a surface, and a badge is a mark *on* a surface: laid
         on a ground of its own colour — a row shaded `mib-branding-50` to say it is the one being
         read — the pill dissolves into it, and the badge becomes text with no badge around it. A
         translucent tint keeps its distance from whatever it sits on, which is what a mark has to
         do. On white it lands within three units of the flat tint, so nothing moves where badges
         already are.

         The 700 ink and not `text-mib-primary`: `indigo-100` is not a tint of our primary — the
         primary is #4966C2, not a Tailwind indigo — and the primary as ink reaches only 4.3 against
         the 4.5 WCAG AA asks of a small label. The 700 holds 6.2 on white and 5.3 on a
         `mib-branding-50` row.
        */
        primary: { bg: 'bg-mib-primary/15', text: 'text-mib-branding-700' },
        secondary: { bg: 'bg-gray-300', text: 'text-gray-700' },
        success: { bg: 'bg-mib-success-surface', text: 'text-mib-success' },
        warning: { bg: 'bg-mib-warning-surface', text: 'text-mib-warning-ink' },
        danger: { bg: 'bg-mib-danger-surface', text: 'text-mib-danger' },
        info: { bg: 'bg-mib-info-surface', text: 'text-mib-info' },
        // `ai` and `primary` read the same in `subtle` — there is only one navy tint light enough to
        // write on — and keep the same 700 ink. Only their grounds differ, and by three units on
        // white. A subtle badge says "quiet", not "which theme": `filled` when it has to read.
        ai: { bg: 'bg-mib-branding-50', text: 'text-mib-branding-700' },
      };
      classes.push(subtle[theme].bg, subtle[theme].text, THEME_SUBTLE_DARK[theme]);
    }

    classes.push(this.shape() === 'square' ? 'rounded-mib' : 'rounded-full');

    return classes.join(' ');
  });
}
